/**
 * Sitout Warning Popup for Poker Reborn
 *
 * Warns player that sitting out while in a hand will forfeit their pot (like folding).
 * Also informs that their seat will be reserved for 2 minutes.
 *
 * @see docs/screens/room/ROOM_BAR.md - Sitout behavior
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, PRIMARY_BUTTON_STYLE, SECONDARY_BUTTON_STYLE } from '@/components/common/Button';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Overlay */
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },

  /** Popup */
  popup: {
    width: 400,
    height: 260,
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 15,
  },

  /** Title */
  title: {
    fill: 0xffffff,
    fontSize: 22,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  /** Warning message */
  message: {
    fill: 0xcccccc,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: 20,
  },

  /** Warning highlight */
  warning: {
    fill: 0xff6b6b,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  /** Info text */
  info: {
    fill: 0x88ccff,
    fontSize: 13,
    fontFamily: 'Roboto, Arial, sans-serif',
  },
};

// =============================================================================
// SITOUT WARNING POPUP
// =============================================================================

export class SitoutWarningPopup extends Container {
  private overlay: Graphics;
  private popup: Container;
  private background: Graphics;
  private titleText: Text;
  private messageText: Text;
  private warningText: Text;
  private infoText: Text;
  private sitoutButton: Button;
  private cancelButton: Button;

  private screenWidth: number;
  private screenHeight: number;

  /** Callback when player confirms sitout */
  onSitout: (() => void) | null = null;

  /** Callback when player cancels */
  onCancel: (() => void) | null = null;

  constructor(screenWidth: number, screenHeight: number) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create overlay
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointerdown', () => { this.handleCancel(); });
    this.addChild(this.overlay);

    // Create popup container
    this.popup = new Container();
    this.popup.x = screenWidth / 2;
    this.popup.y = screenHeight / 2;
    this.addChild(this.popup);

    // Create background
    this.background = new Graphics();
    this.popup.addChild(this.background);
    this.drawBackground();

    // Stop popup clicks from closing
    this.background.eventMode = 'static';
    this.background.on('pointerdown', (e) => { e.stopPropagation(); });

    // Create title
    this.titleText = new Text({
      text: 'Sit Out?',
      style: STYLES.title,
    });
    this.titleText.anchor.set(0.5);
    this.titleText.y = -90;
    this.popup.addChild(this.titleText);

    // Create message
    this.messageText = new Text({
      text: 'You are currently in a hand.',
      style: STYLES.message,
    });
    this.messageText.anchor.set(0.5);
    this.messageText.y = -50;
    this.popup.addChild(this.messageText);

    // Create warning text
    this.warningText = new Text({
      text: 'Sitting out now will fold your hand and forfeit the pot!',
      style: STYLES.warning,
    });
    this.warningText.anchor.set(0.5);
    this.warningText.y = -20;
    this.popup.addChild(this.warningText);

    // Create info text
    this.infoText = new Text({
      text: 'Your seat will be reserved for 2 minutes.',
      style: STYLES.info,
    });
    this.infoText.anchor.set(0.5);
    this.infoText.y = 15;
    this.popup.addChild(this.infoText);

    // Create action buttons
    const buttonWidth = 120;
    const buttonHeight = 44;
    const buttonGap = 20;
    const buttonY = 70;
    const totalButtonsWidth = buttonWidth * 2 + buttonGap;

    // Create cancel button (left) - primary action to stay
    this.cancelButton = new Button({
      label: 'Stay',
      width: buttonWidth,
      height: buttonHeight,
      style: PRIMARY_BUTTON_STYLE,
    });
    this.cancelButton.onClick = () => { this.handleCancel(); };
    this.cancelButton.x = -totalButtonsWidth / 2;
    this.cancelButton.y = buttonY;
    this.popup.addChild(this.cancelButton);

    // Create sitout button (right) - secondary action
    this.sitoutButton = new Button({
      label: 'Sit Out',
      width: buttonWidth,
      height: buttonHeight,
      style: SECONDARY_BUTTON_STYLE,
    });
    this.sitoutButton.onClick = () => { this.handleSitout(); };
    this.sitoutButton.x = -totalButtonsWidth / 2 + buttonWidth + buttonGap;
    this.sitoutButton.y = buttonY;
    this.popup.addChild(this.sitoutButton);
  }

  /**
   * Resize popup
   */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    // Resize overlay
    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    // Reposition popup
    this.popup.x = width / 2;
    this.popup.y = height / 2;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private drawBackground(): void {
    const { width, height, fill, stroke, cornerRadius } = STYLES.popup;

    this.background.clear();
    this.background.roundRect(-width / 2, -height / 2, width, height, cornerRadius);
    this.background.fill(fill);
    this.background.setStrokeStyle({ width: 2, color: stroke });
    this.background.stroke();
  }

  private handleSitout(): void {
    if (this.onSitout) {
      this.onSitout();
    }
    this.destroy();
  }

  private handleCancel(): void {
    if (this.onCancel) {
      this.onCancel();
    }
    this.destroy();
  }
}
